import { Grade, Language, QuestionSubtypesQuery, Question_Type } from "@generated/graphql";
import { QUESTION_TYPE_TITLE_ADJECTIVE } from "constants/global";
import { CompetencyMapSelectedParts } from "helpers/competencyMapSelectParts";
import { formatNumber } from "helpers/index";
import palette from "theme/palette";
import {
  QuestionsSubtypeValue,
  ScopeChip,
  ScopeChipKind,
  ScopeChipMeasureStyle,
  ScopeGroup,
  ScopeQuestionSubtypeOption,
  ScopeSourceInput,
} from "./types";

export const GRADE_DESCRIPTIONS: Record<Grade, string> = {
  [Grade.Junior]: "Базовые знания, определения, типовые задачи и знание инструментов",
  [Grade.Middle]: "Нестандартные задачи, неполные данные, самостоятельные решения",
  [Grade.Senior]: "Системное мышление, архитектурные решения, экспертная глубина",
};

export const GRADE_BUTTONS: Array<{ value: Grade; label: string }> = [
  { value: Grade.Junior, label: "Junior" },
  { value: Grade.Middle, label: "Middle" },
  { value: Grade.Senior, label: "Senior" },
];

export const LANGUAGE_BUTTONS: Array<{ value: Language; label: string }> = [
  { value: Language.Ru, label: "RU" },
  { value: Language.En, label: "EN" },
  { value: Language.Kk, label: "KZ" },
  { value: Language.Uz, label: "UZ" },
];

export const AMOUNT_BUTTONS = ["1", "2", "3"] as const;

export const getQuestionSubtypesData = (
  subtypesGroups: QuestionSubtypesQuery["questionSubtypes"]
): QuestionsSubtypeValue => {
  return subtypesGroups.reduce(
    (subtypesGroupsAcc, subtypeGroup) => ({
      ...subtypesGroupsAcc,
      ...(subtypeGroup.subtypes.length
        ? subtypeGroup.subtypes.reduce(
            (subtypesAcc, subtype, index) => ({
              ...subtypesAcc,
              [`${subtypeGroup.type}_${index}`]: {
                type: subtypeGroup.type,
                subtype,
              },
            }),
            {}
          )
        : { [subtypeGroup.type]: { type: subtypeGroup.type, subtype: undefined } }),
    }),
    {}
  );
};

const QUESTION_TYPE_BADGE_LABEL: Record<Question_Type, string> = {
  [Question_Type.Practical]: "Практ.",
  [Question_Type.Theoretical]: "Теория",
  [Question_Type.MultipleChoiceQuestions]: "Тест",
};

export const getQuestionSubtypesOptions = (data: QuestionsSubtypeValue): ScopeQuestionSubtypeOption[] => {
  return Object.entries(data).map(([key, value]) => ({
    value: key,
    title: value.subtype || QUESTION_TYPE_TITLE_ADJECTIVE[value.type],
    description: value.subtype ? QUESTION_TYPE_TITLE_ADJECTIVE[value.type] : undefined,
    badgeType: value.type,
    badgeLabel: QUESTION_TYPE_BADGE_LABEL[value.type],
  }));
};

export const getChipMeasureStyle = (kind: ScopeChipKind): ScopeChipMeasureStyle => {
  if (kind === "competency") {
    return {
      backgroundColor: "#F5F3FF",
      color: "#7C3AED",
      borderColor: "#DDD6FE",
    };
  }

  if (kind === "knowledge") {
    return {
      backgroundColor: "#FFF7ED",
      color: "#C2410C",
      borderColor: "#FED7AA",
    };
  }

  return {
    backgroundColor: palette.third.main,
    color: palette.primary.main,
    borderColor: palette.divider,
  };
};

const getSelectionSets = (selectParts?: CompetencyMapSelectedParts) => ({
  competencyIds: new Set(selectParts?.competencyIds || []),
  knowledgeBockIds: new Set(selectParts?.knowledgeBockIds || []),
  skillIds: new Set(selectParts?.skillIds || []),
});

const getUniqueChips = (chips: ScopeChip[]) => {
  const seen = new Set<string>();

  return chips.filter((chip) => {
    if (seen.has(chip.id)) return false;

    seen.add(chip.id);
    return true;
  });
};

export const getScopeGroups = ({ competencyMap, selectParts }: ScopeSourceInput): ScopeGroup[] => {
  const selectionSets = getSelectionSets(selectParts);
  const useAllMap = !selectParts;

  const competencyChips: ScopeChip[] = [];
  const knowledgeChips: ScopeChip[] = [];
  const skillChips: ScopeChip[] = [];

  competencyMap?.competences?.forEach((competency) => {
    const isCompetencySelected = selectionSets.competencyIds.has(competency.id);
    let hasSelectedKnowledgeBlock = false;
    let hasSelectedSkill = false;

    competency.knowledgeBlockCompetences?.forEach((knowledgeBlockCompetency) => {
      const { knowledgeBlock } = knowledgeBlockCompetency;
      const isKnowledgeBlockSelected = selectionSets.knowledgeBockIds.has(knowledgeBlock.id);
      const selectedSkills =
        knowledgeBlock.skills?.filter((skill) => useAllMap || selectionSets.skillIds.has(skill.id)) || [];
      const hasSelectedSkillInKnowledgeBlock = selectedSkills.length > 0;

      if (isKnowledgeBlockSelected || hasSelectedSkillInKnowledgeBlock) {
        hasSelectedKnowledgeBlock = true;
      }

      if (hasSelectedSkillInKnowledgeBlock) {
        hasSelectedSkill = true;
      }

      if (useAllMap || isKnowledgeBlockSelected || hasSelectedSkillInKnowledgeBlock) {
        knowledgeChips.push({
          id: knowledgeBlock.id,
          title: knowledgeBlock.title,
          kind: "knowledge",
        });
      }

      selectedSkills.forEach((skill) => {
        skillChips.push({
          id: skill.id,
          title: skill.title,
          kind: "skill",
        });
      });
    });

    if (useAllMap || isCompetencySelected || hasSelectedKnowledgeBlock || hasSelectedSkill) {
      competencyChips.push({
        id: competency.id,
        title: competency.title,
        kind: "competency",
      });
    }
  });

  return [
    {
      id: "competency",
      label: "Компетенции",
      chips: getUniqueChips(competencyChips),
    },
    {
      id: "knowledge",
      label: "Блоки знаний",
      chips: getUniqueChips(knowledgeChips),
    },
    {
      id: "skill",
      label: "Навыки",
      chips: getUniqueChips(skillChips),
    },
  ];
};

export const getScopeAreasCount = (scopeGroups: ScopeGroup[]) =>
  scopeGroups.find((group) => group.id === "skill")?.chips.length || 0;

export const getTotalQuestionsCount = ({
  areasCount,
  selectedTypesCount,
  amount,
}: {
  areasCount: number;
  selectedTypesCount: number;
  amount: string;
}) => areasCount * selectedTypesCount * Number(amount || 0);

export const getCounterFormulaLabel = ({
  areasCount,
  selectedTypesCount,
  amount,
}: {
  areasCount: number;
  selectedTypesCount: number;
  amount: string;
}) => `${areasCount} × ${selectedTypesCount} × ${amount}`;

export const getFooterMetaText = ({
  areasCount,
  selectedTypesCount,
  amount,
}: {
  areasCount: number;
  selectedTypesCount: number;
  amount: string;
}) => {
  if (!selectedTypesCount) return "Выберите хотя бы один тип вопросов";

  return [
    formatNumber(["область", "области", "областей"], areasCount),
    formatNumber(["тип", "типа", "типов"], selectedTypesCount),
    formatNumber(["вопрос на область", "вопроса на область", "вопроса на область"], Number(amount)),
  ].join(" · ");
};

export const createChipMeasurementElement = (text: string, kind: ScopeChipKind) => {
  const element = document.createElement("span");
  const styles = getChipMeasureStyle(kind);

  element.textContent = text;
  element.style.position = "absolute";
  element.style.visibility = "hidden";
  element.style.whiteSpace = "nowrap";
  element.style.pointerEvents = "none";
  element.style.left = "-9999px";
  element.style.top = "-9999px";
  element.style.fontSize = "13px";
  element.style.fontWeight = "500";
  element.style.lineHeight = "18px";
  element.style.padding = "2px 8px";
  element.style.borderRadius = "999px";
  element.style.border = `1px solid ${styles.borderColor}`;
  element.style.backgroundColor = styles.backgroundColor;
  element.style.color = styles.color;
  element.style.fontFamily = "inherit";

  document.body.appendChild(element);

  return element;
};

export const getMeasuredWidth = (text: string, kind: ScopeChipKind) => {
  if (typeof document === "undefined") return 0;

  const element = createChipMeasurementElement(text, kind);
  const width = Math.ceil(element.getBoundingClientRect().width);
  document.body.removeChild(element);

  return width;
};

export const getBadgeStyle = (type: Question_Type) => {
  if (type === Question_Type.Practical) {
    return {
      backgroundColor: "#ECFDF5",
      color: palette.green.contrast,
    };
  }

  if (type === Question_Type.Theoretical) {
    return {
      backgroundColor: palette.third.main,
      color: palette.primary.main,
    };
  }

  return {
    backgroundColor: "#F5F3FF",
    color: "#7C3AED",
  };
};
